/** /support-tickets/:publicId — one support ticket (client MCP plan §5.4.1; WPS FE-03, backend
 *  P1-11). The backend's `console_path`, so the e-mail sink's link and a quoted `TO-…` land here
 *  (the id reads in any case, `TO-` optional).
 *
 *  Read top to bottom: our facts (what we found, the subject, who escalated it and when); the
 *  client's words in ONE block marked untrusted — the summary, the follow-ups and the AI client's
 *  own name, quoted as plain text and never as markup; the bookings it covers; the internal AI
 *  diagnose (its thread, or why none ran and where to start one); the case file; every delivery;
 *  and the status history with its reasons. "Change status…" is offered only to a holder of
 *  `console_support_tickets_manage` (`can_manage`) while the ticket can still move on. */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as ticketsApi from '@/api/supportTickets';
import {
  DIAGNOSE_STATE_LABELS,
  deliveryStatusColor,
  deliveryStatusLabel,
  severityColor,
  severityLabel,
  statusColor,
  statusLabel,
  SUBJECT_SCOPE_LABELS,
  SUPPORT_TICKET_STATUS_AUDIT_EVENT,
  supportTicketKeys,
  type SupportTicketDetail,
} from '@/api/supportTickets';
import { PageHeader } from '@/components/PageHeader';
import { Field, FieldGrid, LinkField, SectionCard } from '@/components/ReadView';
import { UntrustedBlock, UntrustedText } from '@/components/UntrustedText';
import { operatorActions } from '@/components/ValueActions/identity';
import {
  act,
  CopyableId,
  DetailSubtitle,
  utcTitle,
  ValueActions,
} from '@/components/ValueActions/ValueActions';
import { formatDate } from '@/utils/format';

import { CaseFileView } from './CaseFileView';
import { StatusChangeDialog } from './StatusChangeDialog';
import {
  bookingActions,
  momentActions,
  requesterActions,
  severityActions,
  statusActions,
  statusChangesAuditHref,
  tenantGrid,
} from './supportTicketLinks';
import { SinkChips } from './SinkChips';

export function SupportTicketDetailPage() {
  const { publicId = '' } = useParams<{ publicId: string }>();
  const queryKey = supportTicketKeys.detail(publicId);
  const query = useQuery({
    queryKey,
    queryFn: () => ticketsApi.retrieve(publicId),
    enabled: publicId !== '',
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.isError) {
    const status = query.error instanceof ConsoleApiError ? query.error.status : 0;
    return (
      <Box>
        <PageHeader title="Support ticket" helpStory="support-tickets" />
        <Alert severity={status === 404 ? 'info' : 'error'}>
          {status === 404
            ? `There is no support ticket ${publicId}.`
            : status === 403
              ? 'You can’t see support tickets: it needs the console_support_tickets_view permission.'
              : `Couldn’t load the ticket: ${query.error.message}`}
        </Alert>
      </Box>
    );
  }
  return <TicketView ticket={query.data} queryKey={queryKey} />;
}

/** A link to a console path the backend built (`/bookings/12/diagnose?thread=…`); anything else
 *  is shown as text, never followed. */
const consolePath = (path: string | null | undefined): string | null =>
  path && path.startsWith('/') && !path.startsWith('//') ? path : null;

function Moment({ iso }: { iso: string | null }) {
  if (!iso) return null;
  return (
    <ValueActions title={utcTitle(iso)} actions={momentActions(iso)}>
      {formatDate(iso)}
    </ValueActions>
  );
}

function TicketView({
  ticket,
  queryKey,
}: {
  ticket: SupportTicketDetail;
  queryKey: readonly unknown[];
}) {
  const [changing, setChanging] = useState(false);
  const canMove = ticket.can_manage && ticket.next_statuses.length > 0;

  const tenantPart = ticket.tenant ? (
    <ValueActions
      key="tenant"
      actions={operatorActions(ticket.tenant, tenantGrid(ticket.tenant.id))}
    >
      {ticket.tenant.name || ticket.tenant.code}
    </ValueActions>
  ) : (
    `Tenant #${ticket.tour_operator_id}`
  );

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        helpStory="support-tickets"
        title={ticket.public_id}
        titleAdornment={
          <Stack direction="row" spacing={1}>
            <ValueActions chip actions={statusActions(ticket.status)}>
              <Chip
                size="small"
                color={statusColor(ticket.status)}
                label={statusLabel(ticket.status)}
                data-ticket-status
              />
            </ValueActions>
            <ValueActions chip actions={severityActions(ticket.severity)}>
              <Chip
                size="small"
                variant="outlined"
                color={severityColor(ticket.severity)}
                label={severityLabel(ticket.severity)}
              />
            </ValueActions>
          </Stack>
        }
        subtitle={
          <DetailSubtitle
            nowrap
            parts={[
              tenantPart,
              <ValueActions
                key="opened"
                title={utcTitle(ticket.created_ts)}
                actions={momentActions(ticket.created_ts)}
              >
                Opened {formatDate(ticket.created_ts)}
              </ValueActions>,
              <ValueActions key="id" actions={[act.copy(ticket.public_id, 'Copy ticket id')]}>
                {ticket.public_id}
              </ValueActions>,
            ]}
          />
        }
        actions={
          canMove ? (
            <Button variant="contained" onClick={() => setChanging(true)}>
              Change status…
            </Button>
          ) : undefined
        }
      />

      <SectionCard title="The ticket">
        <Field label="What we found" value={ticket.title} />
        <FieldGrid>
          <Field
            label="Finding code"
            value={
              ticket.finding_code ? (
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                  {ticket.finding_code}
                </Box>
              ) : null
            }
          />
          <Field
            label="Subject"
            value={
              <>
                {SUBJECT_SCOPE_LABELS[ticket.subject.scope]}
                {ticket.subject.booking_id !== null && (
                  <>
                    {' of booking '}
                    <ValueActions actions={bookingActions(ticket.subject.booking_id)}>
                      <Link
                        component={RouterLink}
                        to={`/bookings/${ticket.subject.booking_id}`}
                        underline="hover"
                      >
                        #{ticket.subject.booking_id}
                      </Link>
                    </ValueActions>
                  </>
                )}
              </>
            }
          />
          <Field
            label="Escalations"
            value={
              ticket.escalation_count > 1
                ? `${ticket.escalation_count} — the first and ${ticket.escalation_count - 1} follow-up${ticket.escalation_count > 2 ? 's' : ''} while it was open`
                : '1'
            }
          />
          <Field label="Opened" value={<Moment iso={ticket.created_ts} />} />
          <Field label="Last escalated" value={<Moment iso={ticket.last_escalated_ts} />} />
          <Field label="Status changed" value={<Moment iso={ticket.status_changed_ts} />} />
          <Field
            label="Escalated by"
            value={
              ticket.requester.user ? (
                <ValueActions actions={requesterActions(ticket.requester.user)}>
                  {ticket.requester.user.email}
                </ValueActions>
              ) : (
                ticket.requester.kind
              )
            }
          />
          <Field label="Channel" value={ticket.requester.channel} />
          <Field
            label="OAuth client"
            value={<CopyableId value={ticket.requester.oauth_client_id} label="Copy client id" />}
          />
          <Field
            label="MCP grant"
            value={<CopyableId value={ticket.requester.grant_id} label="Copy grant id" />}
          />
        </FieldGrid>
      </SectionCard>

      <Box sx={{ mb: 2 }}>
        <UntrustedBlock title="The client’s words" legend={ticket.client_text.legend}>
          <UntrustedText label="Summary (opened the ticket)" text={ticket.client_text.summary} />
          {ticket.client_text.followups.map((f, i) => (
            <UntrustedText
              // Follow-ups keep their order; `n` may be missing on an old one.
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              label={[
                `Follow-up ${f.n ?? i + 1}`,
                f.at ? formatDate(f.at) : null,
                f.severity ? `severity ${f.severity}` : null,
                f.case_id ? `case ${f.case_id}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
              text={f.summary}
            />
          ))}
          <UntrustedText
            label="The AI client’s name for itself"
            text={ticket.client_text.oauth_client_name}
            empty="No name given."
          />
        </UntrustedBlock>
      </Box>

      <SectionCard
        title={`Bookings (${ticket.bookings.length}${ticket.subject.truncated ? '+' : ''})`}
      >
        {ticket.bookings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No bookings recorded.
          </Typography>
        ) : (
          <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2 }}>
            {ticket.bookings.map((b, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Typography key={i} component="li" variant="body2" data-booking={b.booking_id ?? ''}>
                {b.booking_id !== null ? (
                  <ValueActions actions={bookingActions(b.booking_id)}>
                    {consolePath(b.console_path) ? (
                      <Link
                        component={RouterLink}
                        to={consolePath(b.console_path)!}
                        underline="hover"
                      >
                        Booking #{b.booking_id}
                      </Link>
                    ) : (
                      `Booking #${b.booking_id}`
                    )}
                  </ValueActions>
                ) : (
                  'A booking that is gone'
                )}
                {Object.keys(b.refs).length > 0 && (
                  <Typography variant="body2" component="span" color="text.secondary">
                    {' · '}
                    {Object.entries(b.refs)
                      .map(([k, v]) => `${k} ${v}`)
                      .join(' · ')}
                  </Typography>
                )}
              </Typography>
            ))}
          </Stack>
        )}
        {ticket.subject.truncated && (
          <Typography variant="caption" color="text.secondary">
            The case covered more bookings than it lists.
          </Typography>
        )}
      </SectionCard>

      <DiagnoseSection ticket={ticket} />

      <SectionCard title="Case file">
        <CaseFileView caseFile={ticket.case} />
      </SectionCard>

      <DeliveriesSection ticket={ticket} />

      <StatusHistorySection ticket={ticket} />

      {changing && (
        <StatusChangeDialog
          ticket={ticket}
          queryKey={queryKey}
          onClose={() => setChanging(false)}
        />
      )}
    </Box>
  );
}

function DiagnoseSection({ ticket }: { ticket: SupportTicketDetail }) {
  const { diagnose } = ticket;
  const thread = consolePath(diagnose.path);
  const start = consolePath(diagnose.start_path);
  return (
    <SectionCard title={`Internal AI diagnose · ${DIAGNOSE_STATE_LABELS[ticket.diagnose_state]}`}>
      <Box data-diagnose>
        {thread ? (
          <FieldGrid>
            <LinkField label="Thread" to={thread} text="Open the diagnose thread" />
            <Field
              label="Task"
              value={<CopyableId value={diagnose.task_id} label="Copy task id" />}
            />
          </FieldGrid>
        ) : (
          <Field
            label="Why none ran"
            value={diagnose.skipped_reason || 'No diagnose was started with this ticket.'}
          />
        )}
        {start && (
          <LinkField
            label="Start one"
            to={start}
            text={`The Diagnose tab of the ${SUBJECT_SCOPE_LABELS[ticket.subject.scope].toLowerCase()}`}
          />
        )}
      </Box>
    </SectionCard>
  );
}

function DeliveriesSection({ ticket }: { ticket: SupportTicketDetail }) {
  return (
    <SectionCard title={`Announcements (${ticket.deliveries.length})`}>
      <Box sx={{ mb: 1 }}>
        <SinkChips sinks={ticket.sinks} />
      </Box>
      {ticket.deliveries.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label="Deliveries">
            <TableHead>
              <TableRow>
                <TableCell>Sink</TableCell>
                <TableCell>Announces</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Attempts</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Sent / next try</TableCell>
                <TableCell>Answer</TableCell>
                <TableCell>Last error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticket.deliveries.map((d) => (
                <TableRow key={d.id} data-delivery={d.id}>
                  <TableCell>{d.sink}</TableCell>
                  <TableCell>
                    {d.event === 'followup'
                      ? `Follow-up ${d.followup ?? ''}`.trim()
                      : 'The opening'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={deliveryStatusColor(d.status)}
                      label={deliveryStatusLabel(d.status)}
                    />
                  </TableCell>
                  <TableCell align="right">{d.attempts}</TableCell>
                  <TableCell>{formatDate(d.created_ts)}</TableCell>
                  <TableCell>
                    {d.sent_ts
                      ? formatDate(d.sent_ts)
                      : d.status === 'pending'
                        ? `next ${formatDate(d.next_attempt_ts)}`
                        : '—'}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{d.external_ref || '—'}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{d.last_error || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

function StatusHistorySection({ ticket }: { ticket: SupportTicketDetail }) {
  return (
    <SectionCard title="Status history">
      {ticket.status_history.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Not moved on yet.
        </Typography>
      ) : (
        <TableContainer sx={{ mb: 1 }}>
          <Table size="small" aria-label="Status history">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Change</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticket.status_history.map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={i}>
                  <TableCell>{formatDate(h.at)}</TableCell>
                  <TableCell>
                    {statusLabel(h.from)} → {statusLabel(h.to)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {h.reason}
                  </TableCell>
                  <TableCell>
                    {h.actor?.email ? (
                      <ValueActions
                        actions={requesterActions({ id: h.actor.id, email: h.actor.email })}
                      >
                        {h.actor.email}
                      </ValueActions>
                    ) : h.actor ? (
                      `User #${h.actor.id}`
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {ticket.can_manage ? (
        ticket.next_statuses.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Closed — the ticket can’t move on. A new escalation of its subject opens a new ticket.
          </Typography>
        )
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-no-manage>
          Moving the ticket on needs the console_support_tickets_manage permission.
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" data-audit-hint>
        Every status change is audited: the console{' '}
        <Link component={RouterLink} to={statusChangesAuditHref(ticket.tour_operator_id)}>
          audit log
        </Link>{' '}
        records it with its reason as <code>{SUPPORT_TICKET_STATUS_AUDIT_EVENT}</code>, and the
        tenant’s dashboard Activity log shows the ticket and the two statuses.
      </Typography>
    </SectionCard>
  );
}
